import { useEffect, useRef, useState } from 'react';
import { Shell } from '../Shell';
import { useNav } from '../nav';
import { useAsync } from '../../core/useAsync';
import { cachedLeague, getCurrentRound, getSeasonRounds, postSubmission, spotifySearch } from '../../core/api';
import { gradFor, paceLabel, closesIn } from '../format';
import type { CurrentRound, LeagueInfo, MediaType, SeasonRound, SpotifyResult } from '../../core/types';
import s from './Submit.module.css';

// Submit your one entry for the open round. Audio rounds search Spotify (wired);
// video/image rounds take a link/upload (backend path not built yet, so we say so).
// Wired to GET /api/rounds/current + POST /api/rounds/{id}/submission.

const SearchIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>;

const cover = (r: SpotifyResult) =>
  r.thumbnail_url ? { backgroundImage: `url(${r.thumbnail_url})`, backgroundSize: 'cover' } : { background: gradFor(r.media_ref) };

export function Submit() {
  const nav = useNav();
  const round = useAsync<CurrentRound | null>(getCurrentRound, []);
  const league = useAsync<LeagueInfo>(cachedLeague, []);
  const seasonId = league.data?.season_id ?? null;
  const list = useAsync<SeasonRound[]>(() => (seasonId != null ? getSeasonRounds(seasonId) : Promise.resolve([])), [seasonId]);

  const [q, setQ] = useState('');
  const [rows, setRows] = useState<SpotifyResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState<SpotifyResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const media: MediaType = list.data?.find((r) => r.round_id === round.data?.round_id)?.media_type ?? 'audio';

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const v = q.trim();
    if (v.length < 2) { setRows([]); setSearching(false); return; }
    setSearching(true);
    timer.current = setTimeout(async () => {
      try { setRows(await spotifySearch(v)); } catch { setRows([]); } finally { setSearching(false); }
    }, 320);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [q]);

  async function submit() {
    if (!picked || !round.data || busy) return;
    setBusy(true); setErr(null);
    try {
      await postSubmission(round.data.round_id, picked.media_ref);
      setSent(true);
    } catch {
      setErr('Could not submit — try again.');
    } finally {
      setBusy(false);
    }
  }

  if (round.loading || league.loading || list.loading) return <Shell><div className={s.state}>Loading…</div></Shell>;

  const r = round.data;
  const bar = (
    <div className={s.appbar}>
      <span className={s.ctx}>{league.data?.name ?? 'Submit'}</span>
      <button className={s.menu} aria-label="Menu" onClick={() => nav?.go('home')}>⋯</button>
    </div>
  );

  if (!r || r.phase !== 'SUBMITTING') {
    return <Shell>{bar}<div className={s.state}><b>Submissions aren't open</b>{r?.phase === 'VOTING' ? 'This round has moved on to voting.' : 'Check back when the next round opens for entries.'}</div></Shell>;
  }
  if (sent || r.you?.submitted) {
    return <Shell>{bar}<div className={s.state}><b>Entry submitted ✓</b>You're in for “{r.theme}”. Come back when voting opens.</div></Shell>;
  }
  if (media !== 'audio') {
    return <Shell>{bar}<div className={s.head}><h2>{r.theme} <span className={s.badge} style={{ background: `var(--${media})` }}>{media}</span></h2><p>{r.submissions_in} of {r.expected_players} in</p></div><div className={s.state}>This is a {media} round — {media === 'video' ? 'link' : 'upload'} submissions aren't wired into the new app yet.</div></Shell>;
  }

  return (
    <Shell>
      {bar}
      <div className={s.head}>
        <h2>{r.theme} <span className={s.badge} style={{ background: `var(--${media})` }}>{media}</span></h2>
        <p>{[`${r.submissions_in} of ${r.expected_players} in`, paceLabel(r.pace), closesIn(r.submission_deadline)].filter(Boolean).join(' · ')} · one entry per round</p>
      </div>

      {picked ? (
        <div className={s.pick}>
          <span className={s.cov} style={cover(picked)} />
          <div className={s.pc}>
            <span className={s.lbl}>Your entry</span>
            <b>{picked.title}</b>
            <small>{picked.subtitle}</small>
          </div>
          <button className={s.change} onClick={() => setPicked(null)}>Change</button>
        </div>
      ) : null}

      <div className={s.search}>
        <SearchIcon />
        <input placeholder="Search for a track…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      {searching ? <div className={s.searching}>Searching…</div> : null}
      {!searching && q.trim().length >= 2 && rows.length === 0 ? <div className={s.searching}>No matches for “{q.trim()}”.</div> : null}
      {rows.length ? <div className={s.reslabel}>Results</div> : null}
      <div className={s.results}>
        {rows.map((row) => {
          const on = picked?.media_ref === row.media_ref;
          return (
            <button key={row.media_ref} className={`${s.res}${on ? ' ' + s.on : ''}`} onClick={() => setPicked(row)}>
              <span className={s.rcov} style={cover(row)} />
              <span className={s.rinfo}><b>{row.title}</b><small>{row.subtitle}</small></span>
              <span className={s.add}>{on ? '✓' : '+'}</span>
            </button>
          );
        })}
      </div>
      {!q ? <p className={s.alt}>Search Spotify for your track. Duplicates are flagged at close, never now.</p> : null}

      <div className={s.foot}>
        <span className={s.left}>{err ? <span className={s.err}>{err}</span> : 'One entry per round'}</span>
        <button className={s.submit} disabled={!picked || busy} onClick={submit}>{busy ? 'Submitting…' : 'Submit entry'}</button>
      </div>
    </Shell>
  );
}
